import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { MessageSquare, Video, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { track } from '../utils/firebase';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
      track.resetPassword();
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
        <div className="w-full max-w-md text-center">
          <h2 className="text-2xl font-bold mb-4 text-[#0F172A]">Invalid Reset Link</h2>
          <p className="text-[#475569] mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password">
            <Button className="bg-[#4338CA] hover:bg-[#3730A3] text-white" data-testid="request-new-link-button">
              Request New Link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <MessageSquare className="w-10 h-10 text-[#4338CA]" />
            <Video className="w-10 h-10 text-[#4338CA]" />
          </div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2 font-heading">CallChat</h1>
          <p className="text-[#475569]">Connect, Chat, and Collaborate</p>
        </div>

        <div className="bg-white rounded-md border border-[#E2E8F0] p-8">
          {!success ? (
            <>
              <h2 className="text-2xl font-bold mb-2 text-[#0F172A] font-heading">Set New Password</h2>
              <p className="text-[#475569] text-sm mb-6">Enter your new password below.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="password" data-testid="reset-password-label">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Min 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="reset-password-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword" data-testid="reset-confirm-label">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="reset-confirm-input"
                    className="mt-1"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-md"
                  disabled={loading}
                  data-testid="reset-submit-button"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#10B981]" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-[#0F172A] font-heading">Password Reset!</h2>
              <p className="text-[#475569] text-sm mb-6">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-md"
                data-testid="go-to-login-button"
              >
                Go to Sign In
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
